import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Users, AlertTriangle,
  Stethoscope, UserRound, Home, Heart, MessageCircle,
  CheckCircle, Wallet, ChevronDown, ChevronUp,
  Shield, Award, BookOpen, Languages, MapPin as MapPinIcon, Info, Star,
} from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  providerService,
  Provider,
  ServiceRate,
  ProviderAvailabilitySlot,
} from '@lib/supabase/provider.service'
import {
  appointmentService,
  CreateAppointmentData,
} from '@lib/supabase/appointment.service'
import { patientProfileService, PatientAddress } from '@lib/supabase/patientProfile.service'
import { supabase } from '@lib/supabase/client'
import { useAuth } from '@contexts/AuthContext'
import { usePatientAuth } from '@hooks/usePatientAuth'
import Button from '@components/web/Button'
import { FormField } from '@components/web/FormField'
import { clsx } from 'clsx'
import StatusModal from '../../components/web/StatusModal'
import ProviderReviewsModal from '@components/web/ProviderReviewsModal'
import { queryKeys } from '@lib/queryKeys'
import { analyticsService, getAmountBucket } from '@lib/analytics/posthog.service'

// ---- helpers ----------------------------------------------------------------
const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const SHORT_DAYS   = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const DERMATOLOGY_ICON = '/dermatology.png'

const SERVICE_META: Record<string, { textColor: string; bg: string; icon: any; color: string; borderClass: string }> = {
  'Doctor Visit':      { textColor: 'text-blue-600',    bg: 'bg-blue-50',    icon: Stethoscope,   color: '#2563EB', borderClass: 'border-l-blue-600'    },
  'Nurse Care':        { textColor: 'text-emerald-600', bg: 'bg-emerald-50', icon: UserRound,     color: '#059669', borderClass: 'border-l-emerald-600' },
  'Home Care':         { textColor: 'text-purple-600',  bg: 'bg-purple-50',  icon: Home,          color: '#7C3AED', borderClass: 'border-l-purple-600'  },
  'Specialized Care':  { textColor: 'text-rose-600',    bg: 'bg-rose-50',    icon: Heart,         color: '#DC2626', borderClass: 'border-l-rose-600'    },
  Dermatology:        { textColor: 'text-teal-600',     bg: 'bg-teal-700',   icon: 'dermatology', color: '#0F766E', borderClass: 'border-l-teal-600'    },
  'Chat Consultation': { textColor: 'text-sky-600',     bg: 'bg-sky-50',     icon: MessageCircle, color: '#0EA5E9', borderClass: 'border-l-sky-600'     },
}

function getInitials(name: string) {
  return name.trim().split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase()
}

const URGENCY_OPTIONS = [
  { level: 'normal', label: 'Normal Priority',  desc: 'Standard scheduling',              color: '#2563EB', multiplier: 1.0 },
  { level: 'high',   label: 'High Priority',    desc: 'Needs attention within 24 hours',  color: '#EA580C', multiplier: 1.2 },
  { level: 'urgent', label: 'Urgent Care',      desc: 'Requires immediate attention',     color: '#DC2626', multiplier: 1.5 },
]

const CONTINUATION_PREFIX = '[CHAT_CONTINUATION_OF:'

function toSqlDate(d: Date | string): string {
  const dt = d instanceof Date ? d : new Date(d)
  if (isNaN(dt.getTime())) return new Date().toISOString().slice(0, 10)
  return dt.toISOString().slice(0, 10)
}

function toSqlTime(t: string): string {
  const amPm = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (amPm) {
    let h = parseInt(amPm[1], 10)
    const m = amPm[2]
    const p = amPm[3].toUpperCase()
    if (p === 'PM' && h < 12) h += 12
    if (p === 'AM' && h === 12) h = 0
    return `${h.toString().padStart(2, '0')}:${m}:00`
  }
  const mil = t.match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/)
  if (mil) return `${mil[1]}:${mil[2]}:${mil[3] || '00'}`
  return ''
}

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency', currency: 'NGN', maximumFractionDigits: 0,
  }).format(n)
}

function buildContinuationNotes(notes?: string, continuationOfAppointmentId?: string) {
  const cleanNotes = notes?.trim()

  if (!continuationOfAppointmentId) {
    return cleanNotes || undefined
  }

  const marker = `${CONTINUATION_PREFIX}${continuationOfAppointmentId}]`
  return cleanNotes ? `${marker}\n${cleanNotes}` : marker
}

function generateDates(avail: ProviderAvailabilitySlot[]) {
  const today = new Date()
  const result: Array<{
    label: string; day: string; value: string; fullDate: Date; dayOfWeek: string
  }> = []
  for (let i = 0; i < 14; i++) {
    const d = new Date(today)
    d.setDate(today.getDate() + i)
    const dow = DAYS_OF_WEEK[d.getDay()]
    const slot = avail.find((a) => a.day_of_week === dow && a.is_available)
    if (slot && slot.provider_time_slots?.length > 0) {
      const label = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : SHORT_DAYS[d.getDay()]
      result.push({
        label, day: d.getDate().toString(),
        value: toSqlDate(d),
        fullDate: d,
        dayOfWeek: dow,
      })
    }
  }
  return result
}

function generateSlots(dateInfo: { dayOfWeek: string; fullDate: Date }, avail: ProviderAvailabilitySlot[]) {
  const slot = avail.find((a) => a.day_of_week === dateInfo.dayOfWeek && a.is_available)
  if (!slot?.provider_time_slots?.length) return []
  const now = new Date()
  const isToday = dateInfo.fullDate.toDateString() === now.toDateString()
  const curHour = now.getHours()
  const seen = new Set<string>()
  const times: string[] = []
  for (const ts of slot.provider_time_slots) {
    const [sh] = (ts.start_time || '').split(':').map(Number)
    const [eh] = (ts.end_time || '').split(':').map(Number)
    for (let h = sh; h < eh; h++) {
      if (isToday && h <= curHour) continue
      const t = h === 0 ? '12:00 AM' : h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h - 12}:00 PM`
      if (!seen.has(t)) { seen.add(t); times.push(t) }
    }
  }
  return times
}

function buildFullAddress(addr: PatientAddress | null): string {
  if (!addr) return 'Your home address'
  return [addr.address_line1, addr.address_line2, addr.city, addr.state, addr.country]
    .filter(Boolean).join(', ')
}

// ---- Section wrapper --------------------------------------------------------
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-card border border-border p-5">
      <h3 className="font-semibold text-foreground mb-4">{title}</h3>
      {children}
    </div>
  )
}

// ---- main component ---------------------------------------------------------
export default function BookingPage() {
  const { session, role } = useAuth()
  const { providerId } = useParams<{ providerId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { patient } = usePatientAuth()
  const queryClient = useQueryClient()

  const serviceParam = searchParams.get('service') ?? ''
  const continuationOf = searchParams.get('continuationOf') ?? ''

  // Form state
  const [selectedService, setSelectedService] = useState(serviceParam)
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedTime, setSelectedTime] = useState('')
  const [duration, setDuration] = useState<'single' | 'daily'>('single')
  const [numberOfDays, setNumberOfDays] = useState(1)
  const [numberOfPatients, setNumberOfPatients] = useState(1)
  const [urgency, setUrgency] = useState<'normal' | 'high' | 'urgent'>('normal')
  const [contactNumber, setContactNumber] = useState('')
  const [notes, setNotes] = useState('')
  const [symptoms, setSymptoms] = useState('')
  const [medHistory, setMedHistory] = useState('')
  const [allergies, setAllergies] = useState('')
  const [medications, setMedications] = useState('')
  const [surgeryHistory, setSurgeryHistory] = useState('')
  const [familyHistory, setFamilyHistory] = useState('')
  const [accidentRecord, setAccidentRecord] = useState('')
  const [bloodType, setBloodType] = useState('')
  const [heightCm, setHeightCm] = useState('')
  const [weightKg, setWeightKg] = useState('')
  const [showMedInfo, setShowMedInfo] = useState(false)

  // Booking state
  const [isBooking, setIsBooking] = useState(false)
  const [bookingModal, setBookingModal] = useState<{
    type: 'success' | 'error'
    title: string
    message: string
  } | null>(null)
  const [showReviewsModal, setShowReviewsModal] = useState(false)

  const isChatConsultation = selectedService.toLowerCase() === 'chat consultation'
  const isContinuationRequest = isChatConsultation && Boolean(continuationOf)
  const bookingQueryOwner = session?.user.id ?? patient?.auth_id ?? patient?.id ?? null

  // Load patient EMR once provider is loaded and prefill medical fields
  useEffect(() => {
    if (!session) return
    appointmentService.getPatientEmr().then((emr) => {
      if (!emr) return
      if (emr.currentSymptoms)    setSymptoms(emr.currentSymptoms)
      if (emr.medicalHistory)     setMedHistory(emr.medicalHistory)
      if (emr.allergies)          setAllergies(emr.allergies)
      if (emr.currentMedications) setMedications(emr.currentMedications)
      if (emr.surgeryHistory)     setSurgeryHistory(emr.surgeryHistory)
      if (emr.familyHistory)      setFamilyHistory(emr.familyHistory)
      if (emr.accidentRecord)     setAccidentRecord(emr.accidentRecord)
      if (emr.bloodType)          setBloodType(emr.bloodType)
      if (emr.heightCm != null)   setHeightCm(String(emr.heightCm))
      if (emr.weightKg != null)   setWeightKg(String(emr.weightKg))
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session])

  const bookingSetupQuery = useQuery({
    queryKey: queryKeys.patient.bookingSetup(bookingQueryOwner, providerId),
    enabled: !!providerId && !!session && role === 'patient',
    queryFn: async () => {
      const [provider, rates, availability, walletBalance, addresses, ratingStats, reviews] = await Promise.all([
        providerService.getProviderById(providerId!),
        providerService.getProviderServiceRates(providerId!),
        providerService.getProviderAvailability(providerId!),
        patientProfileService.getWalletBalance(),
        patientProfileService.getAddresses(),
        providerService.getProviderRatingStats(providerId!),
        providerService.getProviderReviews(providerId!),
      ])

      const defaultAddress = addresses.find((address: PatientAddress) => address.is_default) ?? addresses[0] ?? null

      return {
        provider,
        rates,
        availability,
        walletBalance,
        defaultAddress,
        ratingStats,
        reviews,
      }
    },
  })

  const provider: Provider | null = bookingSetupQuery.data?.provider ?? null
  const rates: ServiceRate[] = bookingSetupQuery.data?.rates ?? []
  const availability: ProviderAvailabilitySlot[] = bookingSetupQuery.data?.availability ?? []
  const walletBalance = bookingSetupQuery.data?.walletBalance ?? 0
  const defaultAddress: PatientAddress | null = bookingSetupQuery.data?.defaultAddress ?? null
  const ratingStats = bookingSetupQuery.data?.ratingStats ?? { avgRating: 0, totalReviews: 0 }
  const providerReviews = bookingSetupQuery.data?.reviews ?? []
  const isLoading = bookingSetupQuery.isPending
  const loadError = bookingSetupQuery.isError ? 'Failed to load booking details. Please try again.' : null

  const availDates = useMemo(() => generateDates(availability), [availability])
  const timeSlots = useMemo(() => {
    const dateInfo = availDates.find((date) => date.value === selectedDate)
    if (!dateInfo || availability.length === 0) return []
    return generateSlots(dateInfo, availability)
  }, [availability, availDates, selectedDate])

  useEffect(() => {
    if (patient?.phone_number) setContactNumber(patient.phone_number)
  }, [patient])

  useEffect(() => {
    if (availDates.length === 0) return

    const hasSelectedDate = availDates.some((date) => date.value === selectedDate)
    if (!hasSelectedDate) {
      setSelectedDate(availDates[0].value)
    }
  }, [availDates, selectedDate])

  useEffect(() => {
    if (selectedTime && !timeSlots.includes(selectedTime)) {
      setSelectedTime('')
    }
  }, [selectedTime, timeSlots])

  // Cost calculation
  const activeRate = rates.find(
    (r) => r.is_active && r.service_name.toLowerCase() === selectedService.toLowerCase()
  )
  const isServiceActive = Boolean(activeRate)
  const baseRate = activeRate?.base_rate ?? 0
  const urgencyMult = URGENCY_OPTIONS.find((u) => u.level === urgency)?.multiplier ?? 1
  const totalCost = Math.round(
    isChatConsultation
      ? baseRate
      : baseRate * (duration === 'daily' ? numberOfDays : 1) * numberOfPatients * urgencyMult
  )

  const insufficientFunds = walletBalance < totalCost && totalCost > 0
  const selectedServiceMeta = SERVICE_META[selectedService] ?? SERVICE_META['Doctor Visit']
  const SelectedServiceIcon = selectedServiceMeta.icon === 'dermatology' ? null : (selectedServiceMeta.icon ?? Stethoscope)
  const selectedServiceFee = activeRate?.base_rate != null
    ? formatCurrency(activeRate.base_rate)
    : 'Provider Unavailable'

  // Validate
  const getValidationError = (): string | null => {
    if (!selectedService) return 'Please select a service.'
    if (selectedService && !isServiceActive) return 'This provider has not activated this service. Please go back and select a different service.'
    if (!isChatConsultation) {
      if (!defaultAddress) return 'No home address found. Please add a home address in your profile before booking.'
      if (!selectedDate) return 'Please select a date.'
      if (!selectedTime) return 'Please select a time.'
      if (!symptoms.trim()) return 'Please describe your symptoms.'
      if (!medHistory.trim()) return 'Please provide your medical history.'
      if (!allergies.trim()) return 'Please provide allergy information.'
      if (!medications.trim()) return 'Please provide your current medications.'
    }
    if (!isChatConsultation && !contactNumber.trim()) return 'Please provide a contact number.'
    if (insufficientFunds) return `Insufficient wallet balance. You need ${formatCurrency(totalCost)} but have ${formatCurrency(walletBalance)}.`
    return null
  }

  const handleBook = async () => {
    const err = getValidationError()
    if (err) {
      setBookingModal({
        type: 'error',
        title: 'Booking Failed',
        message: err,
      })
      return
    }
    if (!provider) return

    // Duplicate-chat prevention: if this is a fresh chat (not a continuation),
    // check whether the patient already has a chat record with this provider.
    if (isChatConsultation && !isContinuationRequest) {
      const exists = await appointmentService.checkProviderChatExists(provider.id)
      if (exists) {
        setBookingModal({
          type: 'error',
          title: 'Chat Already Exists',
          message:
            `You already have a chat record with ${provider.name}. ` +
            'Please open your existing chat and use the continuation option to book a new session.',
        })
        return
      }
    }

    setBookingModal(null)
    setIsBooking(true)

    analyticsService.capture('booking_started', {
      source: 'booking_screen',
      service_type: selectedService || 'unknown',
      appointment_type: isChatConsultation ? 'chat' : 'home',
      duration: isChatConsultation ? 'single' : duration,
      urgency_level: urgency,
      amount_bucket: getAmountBucket(totalCost),
      continuation: isContinuationRequest,
      platform: 'web',
    })

    const now = new Date()
    const appointmentLocation = isChatConsultation
      ? 'Chat Consultation'
      : buildFullAddress(defaultAddress)
    const finalNotes = buildContinuationNotes(
      notes.trim() || undefined,
      isContinuationRequest ? continuationOf : undefined
    )

    const data: CreateAppointmentData = {
      providerId: provider.id,
      serviceType: selectedService as any,
      appointmentType: isChatConsultation ? 'chat' : 'home',
      duration,
      scheduledDate: isChatConsultation ? toSqlDate(now) : selectedDate,
      scheduledTime: isChatConsultation
        ? toSqlTime(`${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')} ${now.getHours() >= 12 ? 'PM' : 'AM'}`)
        : toSqlTime(selectedTime),
      location: appointmentLocation,
      notes: finalNotes,
      continuationOfAppointmentId: isContinuationRequest ? continuationOf : undefined,
      urgencyLevel: urgency,
      numberOfDays: duration === 'daily' ? numberOfDays : 1,
      numberOfPatients,
      totalCost,
      destinationLatitude: null,
      destinationLongitude: null,
      destinationAddressSnapshot: !isChatConsultation ? appointmentLocation : null,
      medicalInfo: {
        currentSymptoms:    symptoms,
        medicalHistory:     medHistory,
        allergies,
        currentMedications: medications,
        surgeryHistory:     surgeryHistory || undefined,
        familyHistory:      familyHistory  || undefined,
        accidentRecord:     accidentRecord || undefined,
        bloodType:          bloodType      || undefined,
        heightCm:           heightCm  ? parseFloat(heightCm)  : undefined,
        weightKg:           weightKg  ? parseFloat(weightKg)  : undefined,
      },
    }

    const result = await appointmentService.createAppointment(data)

    if (!result.success) {
      setIsBooking(false)
      analyticsService.capture('booking_failed', {
        source: 'booking_screen',
        error_type: 'service_returned_failure',
        service_type: selectedService || 'unknown',
        appointment_type: isChatConsultation ? 'chat' : 'home',
        amount_bucket: getAmountBucket(totalCost),
        platform: 'web',
      })
      setBookingModal({
        type: 'error',
        title: 'Booking Failed',
        message: result.error ?? 'Booking failed. Please try again.',
      })
      return
    }

    await queryClient.invalidateQueries({ queryKey: queryKeys.patient.walletBalance(patient?.id) })
    await queryClient.invalidateQueries({ queryKey: queryKeys.patient.appointments(patient?.id) })
    await queryClient.invalidateQueries({ queryKey: queryKeys.patient.appointmentPages(patient?.id) })
    await queryClient.invalidateQueries({ queryKey: queryKeys.patient.bookingSetup(patient?.id, provider.id) })

    // Notify patient
    try {
      await (supabase.from('notifications') as any).insert({
        patient_id: patient?.id || undefined,
        type: 'appointment',
        title: isChatConsultation
          ? isContinuationRequest
            ? 'Chat Continuation Requested'
            : 'Chat Consultation Requested'
          : 'Appointment Booked',
        message: isChatConsultation
          ? isContinuationRequest
            ? `Your chat continuation request with ${provider.name} has been submitted.`
            : `Your chat consultation request with ${provider.name} has been submitted.`
          : `Your appointment with ${provider.name} has been booked for ${selectedDate} at ${selectedTime}.`,
        data: JSON.stringify({ appointment_id: result.appointment?.id }),
      })
    } catch { /* non-blocking */ }

    // Notify provider
    try {
      await (supabase.from('notifications') as any).insert({
        provider_id: provider.id,
        type: 'appointment',
        title: isChatConsultation
          ? isContinuationRequest
            ? 'New Chat Continuation Request'
            : 'New Chat Consultation Request'
          : 'New Appointment Request',
        message: isChatConsultation
          ? isContinuationRequest
            ? `You have a new chat continuation request from ${patient?.name || 'a patient'}.`
            : `You have a new chat consultation request from ${patient?.name || 'a patient'}.`
          : `You have a new appointment request from ${patient?.name || 'a patient'} for ${selectedDate} at ${selectedTime}.`,
        data: JSON.stringify({ appointment_id: result.appointment?.id }),
      })
    } catch { /* non-blocking */ }

    setIsBooking(false)
    setBookingModal({
      type: 'success',
      title: isChatConsultation
        ? isContinuationRequest
          ? 'Chat Continuation Requested'
          : 'Chat Consultation Requested'
        : 'Booking Confirmed',
      message: isChatConsultation
        ? isContinuationRequest
          ? `Your chat continuation request with ${provider.name} has been submitted.`
          : `Your chat consultation request with ${provider.name} has been submitted.`
        : `Your appointment request with ${provider.name} has been submitted successfully.`,
    })
  }

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 animate-pulse">
        <div className="h-6 w-16 bg-muted rounded" />
        <div className="h-28 bg-muted rounded-xl" />
        <div className="h-40 bg-muted rounded-xl" />
        <div className="h-36 bg-muted rounded-xl" />
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="p-4 sm:p-6 text-center py-20">
        <AlertTriangle size={32} className="mx-auto text-destructive mb-2" />
        <p className="text-muted-foreground">{loadError}</p>
        <Button className="mt-4" onClick={() => { void bookingSetupQuery.refetch() }}>Retry</Button>
      </div>
    )
  }

  if (!provider) {
    return (
      <div className="p-4 sm:p-6 text-center py-20">
        <p className="text-muted-foreground">Provider not found.</p>
        <Button className="mt-4" onClick={() => navigate('/patient/dashboard')}>Back to Dashboard</Button>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button aria-label="Go back" onClick={() => navigate(-1)} className="p-2 hover:bg-muted rounded-lg transition-colors">
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-bold text-foreground">
          {isChatConsultation
            ? isContinuationRequest
              ? 'Request Chat Continuation'
              : 'Request Chat'
            : 'Book Appointment'}
        </h1>
      </div>

      {/* Provider Card */}
      <div
        className={clsx(
          'bg-card border border-border border overflow-hidden border-l-4',
          SERVICE_META[selectedService]?.borderClass ?? 'border-l-primary'
        )}
      >
        <div className="p-5">
          {/* Avatar + name row */}
          <div className="flex items-start gap-4">
            <div className="relative shrink-0">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center overflow-hidden">
                {provider.profile_image_url ? (
                  <img src={provider.profile_image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-primary font-bold text-xl">{getInitials(provider.name)}</span>
                )}
              </div>
              {provider.is_verified && (
                <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                  <Shield size={11} color="#fff" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base font-bold text-foreground">{provider.name}</h2>
                {provider.is_verified && (
                  <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full font-medium">Verified</span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">{provider.specialty}</p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Star size={14} className="text-yellow-400 fill-yellow-400" />
                <span className="text-sm text-foreground">
                  {ratingStats.avgRating.toFixed(1)} ({ratingStats.totalReviews} reviews)
                </span>
                {ratingStats.totalReviews > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowReviewsModal(true)}
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    View Reviews
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Details rows */}
          <div className="mt-4 space-y-3 pt-4 border-t border-border">
            {provider.qualification && (
              <div className="flex items-start gap-3">
                <Award size={15} className="text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Qualification</p>
                  <p className="text-sm text-foreground">{provider.qualification}</p>
                </div>
              </div>
            )}
            {(provider.experience ?? provider.years_of_experience) != null && (
              <div className="flex items-start gap-3">
                <BookOpen size={15} className="text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Experience</p>
                  <p className="text-sm text-foreground">{provider.experience ?? provider.years_of_experience} Years</p>
                </div>
              </div>
            )}
            {provider.languages && provider.languages.length > 0 && (
              <div className="flex items-start gap-3">
                <Languages size={15} className="text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Languages</p>
                  <p className="text-sm text-foreground">{provider.languages.join(', ')}</p>
                </div>
              </div>
            )}
            {provider.address && (
              <div className="flex items-start gap-3">
                <MapPinIcon size={15} className="text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Address</p>
                  <p className="text-sm text-foreground">{provider.address}</p>
                </div>
              </div>
            )}
          </div>

          {/* About */}
          {(provider.about ?? provider.bio) && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-xs font-semibold text-muted-foreground mb-1">About</p>
              <p className="text-sm text-foreground leading-relaxed">{provider.about ?? provider.bio}</p>
            </div>
          )}

          {/* Specializations */}
          {provider.specializations && provider.specializations.length > 0 && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Specializations</p>
              <div className="flex flex-wrap gap-2">
                {provider.specializations.map((s, i) => (
                  <span key={i} className="text-xs bg-muted text-muted-foreground px-2.5 py-1 rounded-full">{s}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Service unavailable warning */}
      {selectedService && !isServiceActive && (
        <div className="flex items-start gap-3 bg-destructive/10 border border-destructive/30 border p-4">
          <AlertTriangle size={18} className="text-destructive shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-destructive">Service unavailable</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              This provider has not activated <strong>{selectedService}</strong>. Please go back and choose a different provider or service.
            </p>
          </div>
        </div>
      )}

      {/* Service selection (if not pre-selected) */}
      {!serviceParam && (
        <Section title="Select Service">
          <div className="space-y-2">
            {rates.filter((r) => r.is_active).map((rate) => {
              const meta = SERVICE_META[rate.service_name]
              const Icon = meta?.icon ?? Stethoscope
              const isDermatology = rate.service_name === 'Dermatology'
              return (
                <button
                  key={rate.id}
                  onClick={() => setSelectedService(rate.service_name)}
                  className={clsx(
                    'w-full flex items-center justify-between p-3 border border transition-all',
                    selectedService === rate.service_name
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/40'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={clsx('w-9 h-9 border flex items-center justify-center', meta?.bg ?? 'bg-muted')}>
                      {isDermatology ? (
                        <img src={DERMATOLOGY_ICON} alt="" aria-hidden="true" className="h-5 w-5 object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
                      ) : (
                        <Icon size={17} className={meta?.textColor ?? 'text-foreground'} />
                      )}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-foreground">{rate.service_name}</p>
                      {rate.base_rate !== null && (
                        <p className="text-xs text-muted-foreground">{formatCurrency(rate.base_rate)}</p>
                      )}
                    </div>
                  </div>
                  {selectedService === rate.service_name && (
                    <CheckCircle size={18} className="text-primary" fill="currentColor" />
                  )}
                </button>
              )
            })}
          </div>
        </Section>
      )}

      {/* Date + Time (non-chat only) */}
      {selectedService && !isChatConsultation && (
        <>
          <div className="bg-card border border-border p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center shrink-0', selectedServiceMeta.bg)}>
                {selectedService === 'Dermatology' ? (
                  <img src={DERMATOLOGY_ICON} alt="" aria-hidden="true" className="h-5 w-5 object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
                ) : SelectedServiceIcon ? (
                  <SelectedServiceIcon size={18} className={selectedServiceMeta.textColor} />
                ) : null}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-muted-foreground">Selected service</p>
                <p className="text-sm font-semibold text-foreground truncate">{selectedService}</p>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-xs font-semibold text-muted-foreground">Fee</p>
              <p className="text-sm font-semibold text-foreground">{selectedServiceFee}</p>
            </div>
          </div>

          <Section title="Select Date">
            {availDates.length === 0 ? (
              <p className="text-sm text-muted-foreground">No available dates found for this provider.</p>
            ) : (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {availDates.map((d) => (
                  <button
                    key={d.value}
                    onClick={() => setSelectedDate(d.value)}
                    className={clsx(
                      'flex-shrink-0 flex flex-col items-center px-4 py-2.5 rounded-xl border transition-all',
                      selectedDate === d.value
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border hover:border-primary/40'
                    )}
                  >
                    <span className={clsx('text-lg font-bold leading-none', selectedDate !== d.value && 'text-foreground')}>{d.day}</span>
                    <span className={clsx('text-xs mt-0.5', selectedDate !== d.value && 'text-muted-foreground')}>{d.label}</span>
                  </button>
                ))}
              </div>
            )}
          </Section>

          {selectedDate && (
            <Section title="Select Time">
              {timeSlots.length === 0 ? (
                <p className="text-sm text-muted-foreground">No time slots available for this date.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {timeSlots.map((t) => (
                    <button
                      key={t}
                      onClick={() => setSelectedTime(t)}
                      className={clsx(
                        'px-3 py-2 rounded-lg text-sm border transition-all',
                        selectedTime === t
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border hover:border-primary/40 text-foreground'
                      )}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </Section>
          )}

          {/* Duration & Patients */}
          <Section title="Service Options">
            <div className="space-y-4">
              {/* Duration */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Duration</label>
                <div className="flex gap-2">
                  {(['single', 'daily'] as const).map((d) => (
                    <button
                      key={d}
                      onClick={() => setDuration(d)}
                      className={clsx(
                        'flex-1 py-2.5 rounded-xl text-sm border transition-all font-medium',
                        duration === d
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border hover:border-primary/40 text-foreground'
                      )}
                    >
                      {d === 'single' ? 'Single Visit' : 'Daily Care'}
                    </button>
                  ))}
                </div>
                {duration === 'daily' && (
                  <div className="mt-3 flex items-center gap-3">
                    <label className="text-sm text-muted-foreground">Number of days:</label>
                    <div className="flex items-center border border-border rounded-lg overflow-hidden">
                      <button onClick={() => setNumberOfDays(Math.max(1, numberOfDays - 1))} className="px-3 py-2 hover:bg-muted transition-colors text-lg font-bold">−</button>
                      <span className="px-4 py-2 text-sm font-medium text-foreground">{numberOfDays}</span>
                      <button onClick={() => setNumberOfDays(numberOfDays + 1)} className="px-3 py-2 hover:bg-muted transition-colors text-lg font-bold">+</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Number of patients */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block flex items-center gap-2">
                  <Users size={15} /> Number of Patients
                </label>
                <div className="flex items-center border border-border rounded-lg overflow-hidden w-fit">
                  <button onClick={() => setNumberOfPatients(Math.max(1, numberOfPatients - 1))} className="px-3 py-2 hover:bg-muted transition-colors text-lg font-bold">−</button>
                  <span className="px-5 py-2 text-sm font-medium text-foreground">{numberOfPatients}</span>
                  <button onClick={() => setNumberOfPatients(numberOfPatients + 1)} className="px-3 py-2 hover:bg-muted transition-colors text-lg font-bold">+</button>
                </div>
              </div>
            </div>
          </Section>

          {/* Urgency */}
          <Section title="Priority Level">
            <div className="space-y-2">
              {URGENCY_OPTIONS.map((u) => (
                <button
                  key={u.level}
                  onClick={() => setUrgency(u.level as any)}
                  className={clsx(
                    'w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left',
                    urgency === u.level ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/30'
                  )}
                >
                  <div className={clsx(
                    'w-3 h-3 rounded-full shrink-0',
                    u.level === 'normal' ? 'bg-blue-600' : u.level === 'high' ? 'bg-orange-600' : 'bg-red-600'
                  )} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{u.label}</p>
                    <p className="text-xs text-muted-foreground">{u.desc}</p>
                  </div>
                  {urgency === u.level && <CheckCircle size={16} className="text-primary shrink-0" />}
                  {u.multiplier > 1 && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      ×{u.multiplier}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </Section>
        </>
      )}

      {/* Contact */}
      {selectedService && !isChatConsultation && (
        <Section title="Contact Information">
          <FormField
            label="Contact Number"
            type="tel"
            value={contactNumber}
            onChange={(e) => setContactNumber(e.target.value)}
            placeholder="+234 800 000 0000"
          />
          <div className="mt-3">
            <FormField
              label="Additional Notes (optional)"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional information for the provider…"
            />
          </div>
        </Section>
      )}

      {/* Medical Info — required for all booking types */}
      {selectedService && (
        <div className="bg-card border border-border overflow-hidden">
          <button
            onClick={() => setShowMedInfo(!showMedInfo)}
            className="w-full flex items-center justify-between p-5"
          >
            <h3 className="font-semibold text-foreground">Medical Information</h3>
            {showMedInfo ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          {showMedInfo && (
            <div className="px-5 pb-5 space-y-4 border-t border-border pt-4">
              <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-xl text-xs text-blue-700">
                <Info size={13} className="shrink-0 mt-0.5" />
                <span>Your information is pre-filled from your saved medical records. Review and update anything that has changed — your records will be saved for future bookings.</span>
              </div>
              <FormField
                label="Current Symptoms *"
                type="text"
                value={symptoms}
                onChange={(e) => setSymptoms(e.target.value)}
                placeholder="Describe your symptoms or reason for visit"
              />
              <FormField
                label="Medical History *"
                type="text"
                value={medHistory}
                onChange={(e) => setMedHistory(e.target.value)}
                placeholder="Previous conditions, diagnoses, etc."
              />
              <FormField
                label="Allergies *"
                type="text"
                value={allergies}
                onChange={(e) => setAllergies(e.target.value)}
                placeholder="Drug allergies, food allergies, etc. (or 'None')"
              />
              <FormField
                label="Current Medications *"
                type="text"
                value={medications}
                onChange={(e) => setMedications(e.target.value)}
                placeholder="List any medications you're currently taking"
              />
              <FormField
                label="Surgery History"
                type="text"
                value={surgeryHistory}
                onChange={(e) => setSurgeryHistory(e.target.value)}
                placeholder="Past surgeries or procedures (or 'None')"
              />
              <FormField
                label="Family Medical History"
                type="text"
                value={familyHistory}
                onChange={(e) => setFamilyHistory(e.target.value)}
                placeholder="Conditions that run in your family (or 'None')"
              />
              <FormField
                label="Accident Record"
                type="text"
                value={accidentRecord}
                onChange={(e) => setAccidentRecord(e.target.value)}
                placeholder="Previous accidents or injuries (or 'None')"
              />
              <FormField
                label="Blood Type"
                type="text"
                value={bloodType}
                onChange={(e) => setBloodType(e.target.value)}
                placeholder="e.g. O+, A-, B+, AB+"
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Height (cm)"
                  type="number"
                  value={heightCm}
                  onChange={(e) => setHeightCm(e.target.value)}
                  placeholder="e.g. 170"
                />
                <FormField
                  label="Weight (kg)"
                  type="number"
                  value={weightKg}
                  onChange={(e) => setWeightKg(e.target.value)}
                  placeholder="e.g. 70"
                />
              </div>
            </div>
          )}
          {!showMedInfo && (
            <p className="px-5 pb-4 text-xs text-muted-foreground">
              {[symptoms, medHistory, allergies, medications].every((v) => v.trim())
                ? '✓ Medical info provided'
                : '⚠ Required fields — tap to fill in'}
            </p>
          )}
        </div>
      )}

      {/* Address info (non-chat) */}
      {selectedService && !isChatConsultation && (
        <div className="bg-card border border-border p-4 flex items-start gap-3">
          <MapPinIcon size={16} className="text-primary shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-muted-foreground">Service location (your address)</p>
            {defaultAddress ? (
              <p className="text-sm text-foreground mt-0.5">{buildFullAddress(defaultAddress)}</p>
            ) : (
              <p className="text-sm text-destructive mt-0.5">No home address saved. Please add one in your profile to book.</p>
            )}
          </div>
        </div>
      )}

      {/* Cost Summary */}
      {selectedService && totalCost > 0 && (
        <div className="bg-card border border-border p-5 space-y-3">
          <h3 className="font-semibold text-foreground">Cost Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-muted-foreground">
              <span>Base rate</span>
              <span>{formatCurrency(baseRate)}</span>
            </div>
            {!isChatConsultation && duration === 'daily' && (
              <div className="flex justify-between text-muted-foreground">
                <span>Days</span>
                <span>×{numberOfDays}</span>
              </div>
            )}
            {!isChatConsultation && numberOfPatients > 1 && (
              <div className="flex justify-between text-muted-foreground">
                <span>Patients</span>
                <span>×{numberOfPatients}</span>
              </div>
            )}
            {urgency !== 'normal' && !isChatConsultation && (
              <div className="flex justify-between text-muted-foreground">
                <span>{urgency === 'urgent' ? 'Urgent' : 'High'} priority</span>
                <span>×{URGENCY_OPTIONS.find((u) => u.level === urgency)?.multiplier}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-foreground pt-2 border-t border-border">
              <span>Total</span>
              <span>{formatCurrency(totalCost)}</span>
            </div>
          </div>

          {/* Wallet balance check */}
          <div className={clsx(
            'flex items-center justify-between p-3 rounded-xl text-sm',
            insufficientFunds ? 'bg-destructive/10 text-destructive' : 'bg-emerald-50 text-emerald-700'
          )}>
            <div className="flex items-center gap-2">
              <Wallet size={15} />
              <span>Wallet balance</span>
            </div>
            <span className="font-semibold">{formatCurrency(walletBalance)}</span>
          </div>
          {insufficientFunds && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <AlertTriangle size={12} />
              Insufficient balance. Please top up your wallet before booking.
            </p>
          )}
        </div>
      )}

      {/* Book Button */}
      {selectedService && (
        <div className="space-y-3">
          <Button
            className="w-full"
            size="lg"
            onClick={handleBook}
            isLoading={isBooking}
            disabled={isBooking || insufficientFunds}
          >
            {isChatConsultation ? 'Request Chat' : 'Confirm Booking'}
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            {totalCost > 0
              ? `${formatCurrency(totalCost)} will be deducted from your wallet`
              : 'Provider will confirm your appointment'}
          </p>
        </div>
      )}

      {bookingModal && (
        <StatusModal
          type={bookingModal.type}
          title={bookingModal.title}
          message={bookingModal.message}
          primaryLabel={bookingModal.type === 'success' ? (isChatConsultation ? 'View Chats' : 'View Appointments') : 'Close'}
          secondaryLabel={bookingModal.type === 'success' ? 'Stay Here' : undefined}
          onPrimary={() => {
            if (bookingModal.type === 'success') {
              navigate(isChatConsultation ? '/patient/chats' : '/patient/appointments')
              return
            }
            setBookingModal(null)
          }}
          onSecondary={bookingModal.type === 'success' ? () => setBookingModal(null) : undefined}
        />
      )}

      <ProviderReviewsModal
        open={showReviewsModal}
        onClose={() => setShowReviewsModal(false)}
        providerName={provider?.name ?? 'Provider'}
        reviews={providerReviews}
        averageRating={ratingStats.avgRating}
        totalReviews={ratingStats.totalReviews}
      />
    </div>
  )
}
